import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Phone, Check, MapPin, ArrowUpRight } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import Breadcrumb from "@/components/site/Breadcrumb";
import EmergencyBanner from "@/components/site/EmergencyBanner";
import ContactForm from "@/components/site/ContactForm";
import { Reveal } from "@/components/site/Reveal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SERVICES, SERVICE_DETAILS, CITIES, COMPANY } from "@/data/content";
import { setSeo, setJsonLd } from "@/lib/seo";
import api from "@/lib/api";

const SITE = "https://serrurier-lagny-sur-marne.com";

export default function ServicePage() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  const details = service ? SERVICE_DETAILS[service.slug] : null;
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    if (!service || !details) return;
    setSeo({
      title: `${service.title} à Lagny-sur-Marne (77) — ${COMPANY.phoneDisplay}`,
      description: `☎ ${COMPANY.phoneDisplay} — ${service.title} à Lagny-sur-Marne et environs : ${details.intro.slice(0, 120)}… Devis gratuit, intervention en 30 min, 24h/24.`,
      canonical: `${SITE}/services/${service.slug}`,
    });
    setJsonLd({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          name: `${service.title} — Serrurier Lagny sur Marne`,
          serviceType: service.title,
          description: details.intro,
          provider: {
            "@type": "Locksmith",
            name: COMPANY.name,
            telephone: COMPANY.phoneTel,
            url: SITE,
            address: {
              "@type": "PostalAddress",
              streetAddress: "26 Rue Freycinet",
              addressLocality: "Lagny-sur-Marne",
              postalCode: "77400",
              addressRegion: "Seine-et-Marne",
              addressCountry: "FR",
            },
          },
          areaServed: CITIES.map((c) => ({
            "@type": "City",
            name: c.name,
            postalCode: c.cp,
          })),
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Accueil", item: `${SITE}/` },
            { "@type": "ListItem", position: 2, name: "Services", item: `${SITE}/#services` },
            { "@type": "ListItem", position: 3, name: service.title, item: `${SITE}/services/${service.slug}` },
          ],
        },
        {
          "@type": "FAQPage",
          mainEntity: details.faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    });
    window.scrollTo(0, 0);
    api.get("/blog").then(({ data }) => setPosts(data.slice(0, 3))).catch(() => {});
  }, [service, details]);

  if (!service || !details) return <Navigate to="/" replace />;
  const Icon = service.icon;

  return (
    <div className="App relative">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="bg-brand-mist pt-32 pb-16">
          <div className="max-w-6xl mx-auto px-6 lg:px-12">
            <Breadcrumb
              items={[
                { label: "Accueil", to: "/" },
                { label: "Services", to: "/#services" },
                { label: service.title },
              ]}
            />
            <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
              <div>
                <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-brand-golddark">
                  <span className="w-8 h-px bg-brand-gold" /> {service.tagline}
                </span>
                <h1 className="mt-5 font-display font-semibold text-4xl md:text-6xl tracking-tight text-brand-navy leading-[1.05]">
                  {service.title}{" "}
                  <span className="block text-brand-golddark text-3xl md:text-4xl mt-2">
                    à Lagny-sur-Marne (77)
                  </span>
                </h1>
                <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
                  {details.intro}
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-4">
                  <a
                    href={`tel:${COMPANY.phoneTel}`}
                    data-testid="service-call-btn"
                    className="inline-flex items-center justify-center gap-3 bg-brand-gold hover:bg-brand-golddark text-brand-navy px-8 py-4 rounded-full font-semibold text-base transition-colors duration-300 active:scale-[0.97] shadow-lg shadow-brand-gold/20"
                  >
                    <Phone className="w-5 h-5" /> {COMPANY.phoneDisplay}
                  </a>
                  <a
                    href="#contact"
                    className="inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navy2 text-white px-8 py-4 rounded-full font-semibold text-base transition-colors duration-300"
                  >
                    Devis gratuit
                  </a>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden ring-1 ring-brand-navy/10 shadow-[0_50px_120px_-40px_rgba(10,30,63,0.5)]">
                <img
                  src={service.image}
                  alt={`${service.title} — serrurier Lagny-sur-Marne`}
                  className="w-full h-auto block"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Contenu détaillé */}
        <section className="py-20 md:py-24 bg-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-12 space-y-12">
            {details.sections.map((block, i) => (
              <Reveal key={i}>
                <h2 className="font-display font-semibold text-2xl md:text-3xl tracking-tight text-brand-navy leading-tight mb-4">
                  {block.h2}
                </h2>
                {block.p.map((para, j) => (
                  <p key={j} className="text-slate-600 leading-relaxed text-base md:text-lg mb-4">
                    {para}
                  </p>
                ))}
                {block.list && (
                  <ul className="mt-4 grid sm:grid-cols-2 gap-3">
                    {block.list.map((item) => (
                      <li key={item} className="flex items-center gap-3 text-brand-navy">
                        <Check className="w-4 h-4 text-brand-gold shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </Reveal>
            ))}
          </div>
        </section>

        {/* FAQ du service */}
        <section className="py-20 md:py-24 bg-brand-mist" data-testid="service-faq">
          <div className="max-w-3xl mx-auto px-6 lg:px-12">
            <Reveal className="mb-10">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-brand-golddark">
                <span className="w-8 h-px bg-brand-gold" /> Questions fréquentes
              </span>
              <h2 className="mt-4 font-display font-semibold text-3xl md:text-4xl tracking-tight text-brand-navy">
                {service.title} : vos questions
              </h2>
            </Reveal>
            <Accordion type="single" collapsible className="w-full">
              {details.faq.map((f, i) => (
                <AccordionItem key={i} value={`sf-${i}`} className="border-b border-brand-line">
                  <AccordionTrigger
                    data-testid={`service-faq-trigger-${i}`}
                    className="text-left font-display font-medium text-base md:text-lg tracking-tight text-brand-navy hover:text-brand-golddark hover:no-underline py-5"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-base leading-relaxed pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Maillage : villes d'intervention */}
        <section className="py-20 md:py-24 bg-white">
          <div className="max-w-6xl mx-auto px-6 lg:px-12">
            <Reveal className="mb-10">
              <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight text-brand-navy">
                {service.title} : nos villes d'intervention
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                Ce service est disponible 24h/24 dans toutes nos communes, avec un
                délai moyen de 30 minutes.
              </p>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {CITIES.map((c, i) => (
                <Reveal key={c.slug} delay={(i % 3) * 0.05}>
                  <Link
                    to={`/zone/${c.slug}`}
                    data-testid={`service-city-${c.slug}`}
                    className="group flex items-center justify-between rounded-2xl border border-brand-line bg-white p-5 hover:shadow-lg hover:shadow-brand-navy/5 hover:-translate-y-0.5 transition-[box-shadow,transform] duration-300"
                  >
                    <span className="flex items-center gap-3">
                      <MapPin className="w-4 h-4 text-brand-gold" />
                      <span>
                        <span className="block font-display font-medium text-brand-navy">
                          {service.title} à {c.name}
                        </span>
                        <span className="text-xs text-slate-500">{c.cp} · Seine-et-Marne</span>
                      </span>
                    </span>
                    <ArrowUpRight className="w-4 h-4 text-brand-golddark group-hover:rotate-45 transition-transform duration-300" />
                  </Link>
                </Reveal>
              ))}
            </div>

            {/* Maillage : autres services */}
            <div className="mt-14">
              <h3 className="font-display font-semibold text-xl text-brand-navy mb-5">
                Nos autres services de serrurerie
              </h3>
              <div className="flex flex-wrap gap-3">
                {SERVICES.filter((s) => s.slug !== service.slug).map((s) => (
                  <Link
                    key={s.slug}
                    to={`/services/${s.slug}`}
                    data-testid={`service-link-${s.slug}`}
                    className="inline-flex items-center gap-2 rounded-full border border-brand-line bg-white px-5 py-2.5 text-sm font-medium text-brand-navy hover:border-brand-gold hover:text-brand-golddark transition-colors duration-300"
                  >
                    {s.title}
                  </Link>
                ))}
              </div>
            </div>

            {/* Maillage : derniers articles */}
            {posts.length > 0 && (
              <div className="mt-14">
                <h3 className="font-display font-semibold text-xl text-brand-navy mb-5">
                  Nos conseils de serrurier
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  {posts.map((p) => (
                    <Link
                      key={p.slug}
                      to={`/blog/${p.slug}`}
                      data-testid={`service-article-${p.slug}`}
                      className="group rounded-2xl border border-brand-line bg-white overflow-hidden hover:shadow-lg hover:shadow-brand-navy/5 transition-shadow duration-300"
                    >
                      <div className="aspect-[16/9] overflow-hidden">
                        <img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                      <div className="p-4">
                        <div className="font-display font-medium text-brand-navy leading-snug line-clamp-2">
                          {p.title}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        <ContactForm />
      </main>
      <Footer />
      <EmergencyBanner />
    </div>
  );
}
